import React, { useRef, useState } from 'react';
import { useEditorContext, TEXT_ELEMENT_IDS } from '../context/EditorContext';
import type { TemplateLayer, AnchorLayer, AudioLayer, BackgroundLayer, VideoLayer } from '../types';

// ─── Layer type colors & icons ────────────────────────────────

const TYPE_COLOR: Record<string, string> = {
  background: '#6366f1',
  anchor: '#f59e0b',
  logo: '#10b981',
  'top-bar': '#3b82f6',
  'lower-third': '#8b5cf6',
  ticker: '#ec4899',
  'breaking-news-banner': '#ef4444',
  watermark: '#64748b',
  audio: '#06b6d4',
  image: '#84cc16',
  video: '#f97316',
};

function layerColor(type: string): string {
  return TYPE_COLOR[type] ?? '#94a3b8';
}

function LayerTypeIcon({ type }: { type: string }) {
  const color = layerColor(type);
  const iconMap: Record<string, string> = {
    background: 'BG',
    anchor: 'AN',
    logo: 'LO',
    'top-bar': 'TB',
    'lower-third': 'L3',
    ticker: 'TK',
    'breaking-news-banner': 'BN',
    watermark: 'WM',
    audio: '♪',
    image: '🖼',
    video: '▶',
  };
  return (
    <div
      style={{
        width: 22, height: 22, borderRadius: 4,
        background: `${color}22`, border: `1px solid ${color}66`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 8, fontWeight: 700, color, flexShrink: 0,
        fontFamily: 'monospace', letterSpacing: '-0.5px',
      }}
    >
      {iconMap[type] ?? type.slice(0, 2).toUpperCase()}
    </div>
  );
}

// ─── Eye / Lock icons ─────────────────────────────────────────

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function LockIcon({ locked }: { locked: boolean }) {
  return locked ? (
    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  ) : (
    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 019.9-1" />
    </svg>
  );
}

// ─── Drag handle ──────────────────────────────────────────────

function DragHandle() {
  return (
    <div
      style={{
        width: 14, display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 2, cursor: 'grab', flexShrink: 0, padding: '0 1px',
      }}
    >
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: 'flex', gap: 2 }}>
          <div style={{ width: 2, height: 2, borderRadius: 1, background: '#475569' }} />
          <div style={{ width: 2, height: 2, borderRadius: 1, background: '#475569' }} />
        </div>
      ))}
    </div>
  );
}

// ─── Single layer row ─────────────────────────────────────────

// ─── Small audio mute icon ────────────────────────────────────

function MuteIcon({ muted }: { muted: boolean }) {
  return muted ? (
    // Speaker with X
    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  ) : (
    // Speaker with waves
    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M12 6v12m-6.414-3H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    </svg>
  );
}

// ─── Single layer row ─────────────────────────────────────────

function LayerRow({
  layer,
  isSelected,
  isDragOver,
  onSelect,
  onVisibilityToggle,
  onLockToggle,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: {
  layer: TemplateLayer;
  isSelected: boolean;
  isDragOver: boolean;
  onSelect: () => void;
  onVisibilityToggle: () => void;
  onLockToggle: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDrop: () => void;
}) {
  const { updateLayer } = useEditorContext();
  const isVisible = layer.visible !== false;
  const isLocked = (layer as TemplateLayer & { locked?: boolean }).locked === true;

  // Layer types that carry audio and can be muted
  const HAS_AUDIO_TYPES = new Set(['anchor', 'audio', 'video', 'background']);
  const hasAudio = HAS_AUDIO_TYPES.has(layer.type);

  // For 'background' video, default muted = true (silent background by default)
  // For all others, default muted = false
  const defaultMuted = layer.type === 'background';
  const isMuted = hasAudio
    ? ((layer as AnchorLayer | AudioLayer | VideoLayer | BackgroundLayer).muted ?? defaultMuted)
    : false;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 8px',
        borderRadius: 5,
        background: isSelected ? '#1d3a6b' : isDragOver ? '#1e293b' : 'transparent',
        border: `1px solid ${isDragOver ? '#3b82f6' : isSelected ? '#3b82f6' : 'transparent'}`,
        cursor: 'pointer',
        userSelect: 'none',
        opacity: isVisible ? 1 : 0.45,
        transition: 'background 0.1s',
        marginBottom: 1,
      }}
    >
      <DragHandle />
      <LayerTypeIcon type={layer.type} />

      {/* Name */}
      <span style={{
        flex: 1, fontSize: 11, color: isSelected ? '#e2e8f0' : '#94a3b8',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {layer.id}
      </span>

      {/* Z index badge */}
      <span style={{
        fontSize: 9, color: '#475569', fontFamily: 'monospace',
        background: '#1e293b', borderRadius: 3, padding: '1px 4px', flexShrink: 0,
      }}>
        z{layer.position.zIndex}
      </span>

      {/* Audio/video mute toggle — shown for all layer types that carry audio */}
      {hasAudio && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); updateLayer(layer.id, { muted: !isMuted }); }}
          title={isMuted ? 'Unmute audio' : 'Mute audio'}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            color: isMuted ? '#ef4444' : '#22d3ee',
            display: 'flex', alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <MuteIcon muted={isMuted} />
        </button>
      )}

      {/* Lock toggle */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onLockToggle(); }}
        title={isLocked ? 'Unlock layer' : 'Lock layer'}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 2,
          color: isLocked ? '#f59e0b' : '#334155',
          display: 'flex', alignItems: 'center',
        }}
      >
        <LockIcon locked={isLocked} />
      </button>

      {/* Visibility toggle */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onVisibilityToggle(); }}
        title={isVisible ? 'Hide layer' : 'Show layer'}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 2,
          color: isVisible ? '#64748b' : '#334155',
          display: 'flex', alignItems: 'center',
        }}
      >
        <EyeIcon open={isVisible} />
      </button>
    </div>
  );
}

// ─── Text element row (headline / subheadline) ────────────────

function TextElementRow({
  label,
  color,
  isSelected,
  isVisible,
  onSelect,
  onToggleVisibility,
}: {
  label: string;
  color: string;
  isSelected: boolean;
  isVisible: boolean;
  onSelect: () => void;
  onToggleVisibility: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 8px',
        borderRadius: 5,
        background: isSelected ? '#1d3a6b' : 'transparent',
        border: `1px solid ${isSelected ? '#3b82f6' : 'transparent'}`,
        cursor: 'pointer',
        userSelect: 'none',
        opacity: isVisible ? 1 : 0.45,
        marginBottom: 1,
      }}
    >
      {/* Placeholder for drag handle space */}
      <div style={{ width: 14, flexShrink: 0 }} />

      {/* T icon */}
      <div
        style={{
          width: 22, height: 22, borderRadius: 4,
          background: `${color}22`, border: `1px solid ${color}66`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, color, flexShrink: 0,
        }}
      >
        T
      </div>

      {/* Label */}
      <span style={{
        flex: 1, fontSize: 11,
        color: isSelected ? '#e2e8f0' : '#94a3b8',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {label}
      </span>

      {/* Text badge */}
      <span style={{
        fontSize: 9, color: '#475569', fontFamily: 'monospace',
        background: '#1e293b', borderRadius: 3, padding: '1px 4px', flexShrink: 0,
      }}>
        txt
      </span>

      {/* Empty lock placeholder */}
      <div style={{ width: 17 }} />

      {/* Visibility toggle */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}
        title={isVisible ? 'Hide' : 'Show'}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 2,
          color: isVisible ? '#64748b' : '#334155',
          display: 'flex', alignItems: 'center',
        }}
      >
        <EyeIcon open={isVisible} />
      </button>
    </div>
  );
}

// ─── Add Layer Dialog ─────────────────────────────────────────

type AddLayerType = 'image' | 'video';

/** Generate next unused imageN / videoN key */
function nextSlotKey(type: AddLayerType, existingIds: string[]): string {
  let n = 1;
  while (existingIds.includes(`${type}${n}`)) n++;
  return `${type}${n}`;
}

function AddLayerPopup({
  existingIds,
  onAdd,
  onClose,
}: {
  existingIds: string[];
  onAdd: (type: AddLayerType, slotKey: string) => void;
  onClose: () => void;
}) {
  // Count existing overlay layers per type
  const imageCount = existingIds.filter((id) => /^image\d+$/.test(id)).length;
  const videoCount = existingIds.filter((id) => /^video\d+$/.test(id)).length;

  return (
    <div style={{
      position: 'absolute', bottom: '100%', left: 0, right: 0,
      background: '#1e293b', border: '1px solid #334155', borderRadius: 8,
      padding: 10, zIndex: 100, boxShadow: '0 4px 24px #000a',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>
        ADD OVERLAY LAYER
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* Add image */}
        <button
          type="button"
          onClick={() => onAdd('image', nextSlotKey('image', existingIds))}
          style={{
            background: '#14532d', border: '1px solid #166534', borderRadius: 5,
            color: '#86efac', fontSize: 11, fontWeight: 600,
            padding: '7px 10px', cursor: 'pointer', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span>🖼</span>
          <span>Add Image Layer</span>
          <span style={{ marginLeft: 'auto', fontSize: 9, color: '#4ade80', opacity: 0.7 }}>
            → image{imageCount + 1}
          </span>
        </button>

        {/* Add video */}
        <button
          type="button"
          onClick={() => onAdd('video', nextSlotKey('video', existingIds))}
          style={{
            background: '#431407', border: '1px solid #7c2d12', borderRadius: 5,
            color: '#fdba74', fontSize: 11, fontWeight: 600,
            padding: '7px 10px', cursor: 'pointer', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span>▶</span>
          <span>Add Video Layer</span>
          <span style={{ marginLeft: 'auto', fontSize: 9, color: '#fb923c', opacity: 0.7 }}>
            → video{videoCount + 1}
          </span>
        </button>
      </div>

      <div style={{ fontSize: 9, color: '#334155', marginTop: 8 }}>
        {imageCount} image · {videoCount} video overlays · unlimited
      </div>

      <button type="button" onClick={onClose} style={{
        marginTop: 6, width: '100%', background: 'none', border: 'none',
        color: '#475569', fontSize: 10, cursor: 'pointer', paddingTop: 4,
      }}>
        Cancel
      </button>
    </div>
  );
}

// ─── LayerPanel ───────────────────────────────────────────────

export function LayerPanel() {
  const {
    state,
    selectLayer,
    toggleLayerVisibility,
    toggleLayerLock,
    toggleTextElementVisibility,
    reorderLayers,
    addLayer,
    removeLayer,
  } = useEditorContext();

  const [showAdd, setShowAdd] = useState(false);

  const { editableConfig, selectedLayerId } = state;

  const dragSrcIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleAddLayer = (type: AddLayerType, slotKey: string) => {
    if (!editableConfig) return;
    const maxZ = editableConfig.layers.reduce((m, l) => Math.max(m, l.position.zIndex), 0);
    const compW = Number(editableConfig.metadata.resolution.split('x')[0]) || 1920;
    const compH = Number(editableConfig.metadata.resolution.split('x')[1]) || 1080;

    // Find the most recently added layer of the same type (highest slot number)
    // e.g. for type='image', look for image1, image2, image3 … pick the highest
    const sameTypeLayers = editableConfig.layers
      .filter((l) => l.type === type)
      .sort((a, b) => {
        // sort by slot number descending so [0] is the "last added"
        const numA = parseInt(a.id.replace(type, ''), 10) || 0;
        const numB = parseInt(b.id.replace(type, ''), 10) || 0;
        return numB - numA;
      });
    const template = sameTypeLayers[0]; // undefined when no prior layer exists

    // Default position/animation — used when there's no prior layer to copy from
    const defaultPosition = {
      x: Math.round(compW * 0.1),
      y: Math.round(compH * 0.1),
      width: Math.round(compW * 0.8),
      height: Math.round(compH * 0.8),
    };
    const defaultAnimation = {
      enter: 'fade' as const,
      exit: 'fade' as const,
      enterDuration: 15,
      exitDuration: 15,
      enterDelay: 0,
      exitAt: -1,
    };

    const newLayer = {
      id: slotKey,
      type,
      label: slotKey,
      // Copy visible/locked/opacity from template, or use defaults
      visible: template?.visible ?? true,
      locked: template ? (template as TemplateLayer & { locked?: boolean }).locked ?? false : false,
      opacity: template?.opacity ?? 1,
      assetKey: slotKey,
      objectFit: (template as import('../types').ImageLayer | undefined)?.objectFit ?? 'cover',
      // Copy idle animation settings if it was an image layer
      ...(type === 'image' && template
        ? {
            idleAnimation: (template as import('../types').ImageLayer).idleAnimation,
            idleSpeed: (template as import('../types').ImageLayer).idleSpeed,
          }
        : {}),
      position: {
        // Copy x/y/width/height from template; always assign a new zIndex on top
        x: template?.position.x ?? defaultPosition.x,
        y: template?.position.y ?? defaultPosition.y,
        width: template?.position.width ?? defaultPosition.width,
        height: template?.position.height ?? defaultPosition.height,
        zIndex: maxZ + 1,
      },
      animation: template
        ? {
            enter: template.animation.enter,
            exit: template.animation.exit,
            enterDuration: template.animation.enterDuration,
            exitDuration: template.animation.exitDuration,
            enterDelay: template.animation.enterDelay,
            exitAt: template.animation.exitAt,
          }
        : defaultAnimation,
    };
    addLayer(newLayer as import('../types').TemplateLayer);
    setShowAdd(false);
  };

  if (!editableConfig) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#475569', fontSize: 12 }}>
        No template loaded
      </div>
    );
  }

  // Sort layers by zIndex descending (top of stack first in UI)
  const sortedLayers = [...editableConfig.layers].sort(
    (a, b) => b.position.zIndex - a.position.zIndex
  );

  const headlineVisible = editableConfig.headline?.visible ?? true;
  const subheadlineVisible = editableConfig.subheadline?.visible ?? true;

  const handleDragStart = (idx: number) => {
    dragSrcIdx.current = idx;
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };

  const handleDrop = (targetIdx: number) => {
    const srcIdx = dragSrcIdx.current;
    if (srcIdx === null || srcIdx === targetIdx) {
      dragSrcIdx.current = null;
      setDragOverIdx(null);
      return;
    }
    // Reorder the sorted list, then sync zIndex values
    const reordered = [...sortedLayers];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    // Reassign zIndex: bottom item = 1, top = n
    const total = reordered.length;
    const withZ = reordered.map((l, i) => ({
      ...l,
      position: { ...l.position, zIndex: total - i },
    }));

    reorderLayers(withZ);
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  };

  const handleDragEnd = () => {
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  };

  // Dynamic image/video layers (can be deleted)
  const dynamicTypes = new Set<string>(['image', 'video']);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '8px 10px 6px',
        borderBottom: '1px solid #1e293b',
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Layers
        </span>
        <span style={{ fontSize: 9, color: '#334155' }}>
          {sortedLayers.length + 2}
        </span>
      </div>

      {/* Layer list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px' }}>
        {/* Text elements always at top */}
        <TextElementRow
          label="Headline"
          color="#eab308"
          isSelected={selectedLayerId === TEXT_ELEMENT_IDS.HEADLINE}
          isVisible={headlineVisible}
          onSelect={() => selectLayer(TEXT_ELEMENT_IDS.HEADLINE)}
          onToggleVisibility={() => toggleTextElementVisibility(TEXT_ELEMENT_IDS.HEADLINE)}
        />
        <TextElementRow
          label="Subheadline"
          color="#22d3ee"
          isSelected={selectedLayerId === TEXT_ELEMENT_IDS.SUBHEADLINE}
          isVisible={subheadlineVisible}
          onSelect={() => selectLayer(TEXT_ELEMENT_IDS.SUBHEADLINE)}
          onToggleVisibility={() => toggleTextElementVisibility(TEXT_ELEMENT_IDS.SUBHEADLINE)}
        />

        {/* Separator */}
        {sortedLayers.length > 0 && (
          <div style={{ height: 1, background: '#1e293b', margin: '4px 0' }} />
        )}

        {/* Regular layers */}
        {sortedLayers.map((layer, i) => (
          <div key={layer.id} style={{ position: 'relative' }}>
            <LayerRow
              layer={layer}
              isSelected={selectedLayerId === layer.id}
              isDragOver={dragOverIdx === i}
              onSelect={() => selectLayer(layer.id)}
              onVisibilityToggle={() => toggleLayerVisibility(layer.id)}
              onLockToggle={() => toggleLayerLock(layer.id)}
              onDragStart={() => handleDragStart(i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDragEnd={handleDragEnd}
              onDrop={() => handleDrop(i)}
            />
            {/* Delete button — only for dynamic image/video layers */}
            {dynamicTypes.has(layer.type) && selectedLayerId === layer.id && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeLayer(layer.id); }}
                title="Remove layer"
                style={{
                  position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
                  background: '#450a0a', border: 'none', borderRadius: 3,
                  color: '#fca5a5', fontSize: 9, padding: '1px 5px', cursor: 'pointer',
                }}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Footer — Add Layer button */}
      <div style={{
        padding: '6px 8px',
        borderTop: '1px solid #1e293b',
        flexShrink: 0,
        position: 'relative',
      }}>
        {showAdd && editableConfig && (
          <AddLayerPopup
            existingIds={editableConfig.layers.map((l) => l.id)}
            onAdd={handleAddLayer}
            onClose={() => setShowAdd(false)}
          />
        )}
        <button
          type="button"
          onClick={() => setShowAdd((s) => !s)}
          style={{
            width: '100%', padding: '5px 0', fontSize: 10, fontWeight: 600,
            borderRadius: 5, border: '1px dashed #334155', cursor: 'pointer',
            background: 'transparent', color: '#64748b',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}
        >
          + Add Image / Video Layer
        </button>
        <div style={{ fontSize: 9, color: '#1e293b', marginTop: 3, textAlign: 'center' }}>
          Drag to reorder · select to resize
        </div>
      </div>
    </div>
  );
}
